/** RoyalEngagement — Event Programme Section */

import React from 'react';
import { motion } from 'framer-motion';
import { CalendarDays, Clock, MapPin } from 'lucide-react';
import { ROYAL_EASE } from '../theme';
import type { RoyalContent } from '../content';

export function ProgrammeSection({
  content,
}: {
  content: RoyalContent['programme'];
}) {
  return (
    <section id="programme" className="relative w-full px-5 py-20 sm:px-8 sm:py-24 lg:px-12 lg:py-32 bg-[var(--royal-ivory)] text-[var(--royal-ink)]">
      <div className="mx-auto w-full max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 36 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7, ease: ROYAL_EASE }}
          className="flex flex-col gap-4 items-center text-center"
        >
          <span className="inline-flex items-center gap-2 text-[0.7rem] font-semibold uppercase tracking-[0.32em] text-[var(--royal-maroon)]">
            <span className="h-px w-6 bg-[var(--royal-gold)]" />
            {content.label}
          </span>
          <h2 className="font-serif text-[clamp(2rem,5vw,3.4rem)] font-semibold leading-[1.08] text-[var(--royal-ink)]">
            {content.title}
          </h2>
        </motion.div>

        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {content.events.map((event, i) => (
            <motion.article
              key={`${event.title}-${i}`}
              whileHover={{ y: -10 }}
              transition={{ duration: 0.4, ease: ROYAL_EASE }}
              className="flex h-full flex-col overflow-hidden rounded-3xl border border-[var(--royal-border)] bg-[var(--royal-ivory-dark)] shadow-[0_28px_64px_-44px_rgba(31,10,14,0.55)] relative"
            >
              {event.image ? (
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={event.image}
                    alt={event.title}
                    className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                  />
                  <span className="absolute left-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-[var(--royal-gold)]/60 text-sm font-semibold text-[var(--royal-maroon)] shadow-lg backdrop-blur-sm">
                    {i + 1}
                  </span>
                </div>
              ) : null}

              <div className="flex flex-1 flex-col gap-3 p-6">
                <h3 className="font-serif text-xl font-semibold text-[var(--royal-ink)]">
                  {event.title}
                </h3>
                <p className="text-sm leading-relaxed text-[var(--royal-muted)]">
                  {event.description}
                </p>
                <ul className="mt-auto flex flex-col gap-2 border-t border-[var(--royal-border)] pt-4 text-sm text-[var(--royal-ink)]">
                  <li className="flex items-center gap-2">
                    <CalendarDays size={15} className="text-[var(--royal-gold)]" />
                    {event.date}
                  </li>
                  {event.time ? (
                    <li className="flex items-center gap-2">
                      <Clock size={15} className="text-[var(--royal-gold)]" />
                      {event.time}
                    </li>
                  ) : null}
                  <li className="flex items-center gap-2">
                    <MapPin size={15} className="text-[var(--royal-gold)]" />
                    {event.venue}
                  </li>
                </ul>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
